import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import DetailsIcon from '@material-ui/icons/Description';
import FilterIcon from '@material-ui/icons/FilterList';
import { DataTable, DynamicForm, FieldType } from '../../../components';
import { ThreeBtn } from '../../../components';
import { useRoutes, useApi, useAuth } from '../../../hooks';
import { Emargement } from '../../../types/emargement';
import { useResidences } from '../../../hooks/theme.hook';

const useStyles = makeStyles((theme) => ({
  topArea: {
    display: 'flex',
    gap: `${theme.spacing(2)}px`,
  },
  separator: {
    flex: 1,
  },
  section: {
    maxWidth: '100%',
    marginTop: theme.spacing(3),
  },
}));

type DataListProps = {
  formPath: string;
  columns: any[];
  data?: any[];
  filterFields: { [key: string]: FieldType };
};

export const EmargmentList: React.FC = () => {
  const { isExpert, isAgent } = useAuth();
  const { getEmargement } = useApi();
  const { residenceOptions } = useResidences();
  const [managers, setManagers] = useState<any>([]);
  const { getManagerList } = useApi();
  const getManagers = useCallback(async () => {
    try {
      const managers = await getManagerList();
      setManagers(managers);
    } catch (error) {
      console.log('get anomalies error', error);
    }
  }, [getManagerList]);
  useEffect(() => {
    getManagers();
  }, [getManagers]);

  const { navigate, setQueryParams, queryParams, paths } = useRoutes();
  const classes = useStyles();
  const [showFiltres, setShowFiltres] = useState(false);
  const [filtres, setFiltres] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const { auth } = useAuth();
  const [role = ''] = auth?.roles || [];
  const [data, setData] = useState<{
    [key: number]: Emargement[];
    maxPages: number;
    maxResultPage: number;
  }>();
  const [loading, setLoading] = useState(false);

  const emargements = useMemo(() => {
    if (data) {
      return (data[currentPage] || []).map((emargement) => ({
        ...emargement,
        agent:
          emargement.agentFirstName || emargement.agentLastName
            ? `${emargement.agentFirstName || ''} ${emargement.agentLastName || ''}`
            : "En attente d'envoi",
      }));
    }
    return [];
  }, [data, currentPage]);

  const navigateToCreate = navigate(paths.newEmargement.path);

  const handleOnAction = useCallback(
    ({ id: emargementId, agentUserName }) => {
      let path = `${paths.emargement.path}/${emargementId}`;
      if (!isAgent) {
        path = `${path}?agentCPCode=${agentUserName}`;
      }
      navigate(path)();
    },
    [isAgent, navigate, paths]
  );

  const handleOnSearch = useCallback(
    (filtres) => {
      if (filtres) {
        const queryParams: any = Object.keys(filtres)
          .filter((key) => filtres[key])
          .reduce((acc, next) => ({ ...acc, [next]: filtres[next] }), {});
        setQueryParams(queryParams);
      }
    },
    [setQueryParams]
  );

  const handleOnSearchClick = useCallback(() => {
    handleOnSearch(filtres);
  }, [handleOnSearch, filtres]);

  const handleOnChange = useCallback((form) => {
    setFiltres(form.values);
  }, []);

  const toggleFiltres = useCallback(() => {
    setShowFiltres((show) => !show);
  }, [setShowFiltres]);

  const handleOnPageChange = useCallback(
    (page) => {
      const currentFiltres = { ...filtres, pageNumber: page + 1 };
      handleOnSearch(currentFiltres);
    },
    [handleOnSearch, filtres]
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { pageNumber, emargements, maxPages, maxResultPage } = await getEmargement({
          maxResultPage: 20,
          pageNumber: 1,
          ...queryParams,
        });
        setCurrentPage(pageNumber);
        setData({ [pageNumber]: emargements, maxPages, maxResultPage });
        setLoading(false);
      } catch (error) {
        setData({ maxPages: 0, maxResultPage: 0 });
        setLoading(false);
      }
    })();
  }, [getEmargement, queryParams]);
  type Obj = { [key: string]: FieldType } | any;
  const object: Obj = {
    creationStartDate: {
      label: 'Date de création du',
      type: 'date',
      maxDateField: 'circulationEndDate',
      grid: 3,
    },
    creationEndDate: {
      label: 'AU',
      type: 'date',
      minDateField: 'circulationStartDate',
      grid: 3,
    },
    id: {
      label: "Numero d'emargement",
      type: 'text',
      grid: 3,
    },
    title: {
      label: 'titre',
      type: 'text',
      grid: 3,
    },
    agentCPCode: {
      label: 'Numéro de CP',
      type: 'text',
      grid: 3,
    },
    residenceShortCode: {
      label: 'Résidence',
      type: 'select',
      options: residenceOptions,
      grid: 3,
    },
    isValidated: {
      label: 'Statut',
      type: 'select',
      options: [
        {
          label: 'Validé',
          value: 'true',
        },
        {
          label: 'Non-validé',
          value: 'false',
        },
      ],
      grid: 3,
    },
    authorCPCode: {
      label: 'Managers',
      type: 'select',
      name: 'manager',
      options: managers.map((x: any) => {
        return {
          label: x.firstName + ' ' + x.lastName,
          value: x.UseName,
        };
      }),
      grid: 3,
    },
  };
  const initial = {
    creationStartDate: null,
    creationEndDate: null,
    id: '',
    title: '',
    agentCPCode: '',
    residenceShortCode: '',
    isValidated: '',
    authorCPCode: '',
  }
  if (role === 'Agent' || role === 'Manager') delete object['authorCPCode'];
  return (
    <div>
      <div className={classes.topArea}>
        {isExpert && (
          <Button color="primary" variant="contained" onClick={navigateToCreate}>
            Nouveau
          </Button>
        )}

        <span className={classes.separator}></span>
        <Button color="primary" startIcon={<FilterIcon />} onClick={toggleFiltres}>
          filtres
        </Button>
      </div>

      {showFiltres && (
        <DynamicForm
          initialValue={{ ...initial, ...queryParams }}
          fields={object}
          onChange={handleOnChange}
          onSubmit={() => { }}
        >
          {(form: any) => (
            <ThreeBtn
              primaryTitle="Rechercher"
              // isPrimarySubmitting={form.isSubmitting}
              primaryOnClick={handleOnSearchClick}
              secondaryTitle="Effacer les filtres"
              // isSecondarySubmitting={form.isSubmitting}
              secondaryOnClick={async () => {
                Object.keys(object).reduce(
                  (acc, key) => ({ ...acc, [key]: object[key] === 'text' ? form.setFieldValue(key, '') : form.setFieldValue(key, null) }),
                  {}
                );



              }}
              disabled={form.isSubmitting}
            />
          )}
        </DynamicForm>
      )
      }

      <div className={classes.section}>
        <DataTable
          columns={[
            {
              label: 'ID',
              dataKey: 'id',
            },
            {
              label: 'Date de creation',
              dataKey: 'creationDate',
            },
            {
              label: 'Date/heure de validation',
              dataKey: 'validationDate',
            },
            {
              label: 'Agent',
              dataKey: 'agent',
            },
            {
              label: 'Residence',
              dataKey: 'residence',
            },
            {
              label: 'titre',
              dataKey: 'title',
            },
            {
              label: 'Statut',
              dataKey: 'isValidated',
              renderValue: (value) => (value ? 'Validé' : 'Non-validé'),
            },
            {
              label: 'Actions',
              renderColumn: (row: Emargement) => (
                <IconButton color="primary" onClick={() => handleOnAction(row)}>
                  <DetailsIcon fontSize="small" />
                </IconButton>
              ),
            },
          ]}
          data={emargements}
          maxPages={data && data.maxPages}
          maxResults={data && data.maxResultPage}
          page={currentPage}
          onPageChange={handleOnPageChange}
          loading={loading}
          onSelect={(row) => handleOnAction(row)}
        />
      </div>
    </div >
  );
};

export default EmargmentList;
